import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import ReactPixel from 'react-facebook-pixel';
import TagManager from 'react-gtm-module';

import App from 'web-containers/AppContainer';
import config from 'web-config';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'draft-js/dist/Draft.css';

import './i18n';
import store from './store';
import pjson from '../package.json';

const env = process.env.TARGET_ENV;
if (env !== 'development') {
  Sentry.init({
    dsn: config.sentry.dsn,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    release: pjson.version,
    beforeSend(event: any, hint: any) {
      // eslint-disable-next-line
      const message =
        event.exception.values[0].value || hint.originalException.message;
      if (
        message &&
        message.startsWith('Non-Error promise rejection captured')
      ) {
        // We want to ignore those kind of errors
        return null;
      }
      return event;
    },
    ignoreErrors: ["Can't find variable: gmo"],
  });
}

if (env === 'production') {
  ReactPixel.init(config.facebook.pixel_id);
  TagManager.initialize({
    gtmId: config.gtm.account,
  });
}

const container = window.document.getElementById('root') as HTMLElement;
const root = createRoot(container);

function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }
  return true;
}
root.render(
  <Provider store={store}>
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <App />
    </StyleSheetManager>
  </Provider>,
);
